import { isAxiosError } from 'axios'
import { create } from 'zustand'
import { lessonsApi } from '@/api/lessons'
import type { LessonStatusChangedPayload } from '@/realtime/useLessonRealtime'
import type { ActiveLesson } from '@/types'

interface ActiveLessonState {
  /** Текущий урок пользователя для глобальной плашки (GET /lessons/active); null — плашки нет. */
  lesson: ActiveLesson | null
  /** meta.next_refresh_at: ближайший момент, когда ответ может смениться без событий урока. */
  nextRefreshAt: string | null
  /**
   * Сколько запросов подряд сорвалось временным сбоем (см. isTransientFetchError:
   * сеть, 5xx при деплое API, 408, 429); 0 — последний ответ успешный или сбой
   * постоянный. Первые сбои подряд (FAILED_FETCH_RETRY_ATTEMPTS в
   * useActiveLessonRealtime) повторяются быстрой серией с растущей паузой — после
   * первого запроса после входа и после фонового, только в видимой вкладке и в
   * сети; таймер next_refresh_at в это время молчит. Серия кончилась — повторов
   * по таймеру нет: счётчик растёт дальше только от запросов по событиям урока,
   * возврату во вкладку, `online` и таймеру next_refresh_at и обнуляется первым
   * успешным ответом. Постоянный сбой после временных тоже обнуляет счётчик:
   * повтора нет, таймер next_refresh_at снова взводится на сохранённый момент, а
   * следующий временный сбой начнёт серию с первой паузы. При нулевом счётчике
   * постоянный сбой стор не меняет.
   */
  failedAttempts: number
  /**
   * Номер применённого ответа API: растёт с каждым ответом, даже если
   * next_refresh_at в нём не сменился, — по нему перевзводится таймер обновления.
   */
  responseVersion: number
  fetch: () => Promise<void>
  /**
   * Событие `lesson.status.changed` урока на плашке — применяется сразу, до
   * ответа API: завершённый или отменённый урок гасит плашку, старт переводит
   * её в «идёт».
   */
  applyStatusChange: (payload: LessonStatusChangedPayload) => void
  reset: () => void
}

const initialState = {
  lesson: null as ActiveLesson | null,
  nextRefreshAt: null as string | null,
  failedAttempts: 0,
  responseVersion: 0,
}

/**
 * Монотонный счётчик запросов: применяется только ответ последнего fetch,
 * а reset отбрасывает ещё летящие — урок прежнего пользователя не всплывёт.
 */
let fetchSeq = 0

/**
 * Сбой, который может пройти сам: ответа нет (сеть, таймаут, обрыв при деплое),
 * 5xx, 408 или 429. На остальные 4xx (404 после отката API, 401 после неудачного
 * обновления токена) и на ответ не того вида повтор получил бы то же самое.
 */
function isTransientFetchError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  const status = error.response?.status
  return status === undefined || status >= 500 || status === 408 || status === 429
}

export const useActiveLessonStore = create<ActiveLessonState>((set, get) => ({
  ...initialState,

  fetch: async () => {
    const seq = ++fetchSeq
    try {
      const response = await lessonsApi.active()
      if (seq !== fetchSeq) return
      set((state) => ({
        lesson: response.data,
        nextRefreshAt: response.meta.next_refresh_at,
        failedAttempts: 0,
        responseVersion: state.responseVersion + 1,
      }))
    } catch (error) {
      // Плашка — подсказка, а не функция страницы: сбой — молча, без тоста.
      // Урок и момент перезапроса не трогаем: сбой фонового запроса не гасит уже
      // показанную плашку и подписку на канал её урока, а после сбоя первого
      // запроса их и так нет. Временный сбой увеличивает счётчик — пока не кончилась
      // быстрая серия, он запускает повтор с растущей паузой. Постоянный повтором не
      // лечится. Если до него шла
      // серия временных, счётчик обнуляется: иначе он остался бы больше нуля и
      // держал выключенным таймер next_refresh_at без единого повтора, а так таймер
      // снова взводится на сохранённый момент. Если счётчик уже 0, стор не
      // меняется: ждущий таймер остаётся, а сработавший (сорвался его же запрос)
      // не перевзводится. Дальше плашку обновят возврат во вкладку, сеть и события
      // урока.
      if (seq !== fetchSeq) return
      if (!isTransientFetchError(error)) {
        if (get().failedAttempts > 0) set({ failedAttempts: 0 })
        return
      }
      set((state) => ({ failedAttempts: state.failedAttempts + 1 }))
    }
  },

  applyStatusChange: (payload) => {
    const { lesson } = get()
    // Событие другого урока — хвост канала, который плашка уже покидает
    if (!lesson || lesson.id !== payload.lesson_id) return

    // Событие новее летящих запросов: их ответы прочитаны до смены статуса
    // и не должны вернуть прежнее состояние
    fetchSeq += 1

    if (payload.status === 'planned' || payload.status === 'in_progress') {
      set({
        lesson: { ...lesson, status: payload.status, actual_start_at: payload.actual_start_at },
      })
      return
    }

    // Урок завершён или отменён — плашку гасим сразу, не дожидаясь ответа API
    set({ lesson: null, nextRefreshAt: null })
  },

  reset: () => {
    fetchSeq += 1
    set({ ...initialState })
  },
}))
